import { ApolloError, DocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { TemplateType } from 'constants/templateTypes';
import { selectIsRokuTemplatingFeatureFlagOn } from 'core/userContext';
import { TemplateAndContent, TemplateLocaleInput } from 'modules/graphql/types';
import { useGraphql } from 'utils/hooks/useGraphql';
import { isDefined } from 'utils/tsHelpers';

import {
  GqlTemplateResponse,
  GqlTemplateResponseTypes,
  SelectedMessageMedium,
  TemplateCampaign,
  UnselectedMessageMedium,
} from '../common/types';
import { useCampaignQueryContext } from '../context/CampaignQueryContext';
import { prepInitialData } from './utils';

export type FetchTemplateSingleContentByIdQueryRequest = {
  id: number;
  locale: TemplateLocaleInput;
};

/**
 * Raw response from GQL.
 */
export type FetchTemplateSingleContentByIdQueryResponse = {
  templateAndSingleContent: GqlTemplateResponse;
};

export type UseTemplateEditSingleContentById = {
  errorMessage?: ApolloError | string;
  hasError: boolean;
  isLoading: boolean;
  isAssociatedWithActiveExperiment: boolean;
  medium: SelectedMessageMedium;
  messageTypeId: number;
  shortenLinks?: boolean;
  templateType?: TemplateType;
  previousDataLocale?: string;
  previousDataTemplateId?: number;
  refetch: (
    variables?: Partial<FetchTemplateSingleContentByIdQueryRequest>,
  ) => Promise<UseTemplateEditSingleContentById>;
  results: string;
};

export const fetchTemplateSingleContentByIdQuery = gql`
  query FetchTemplateSingleContentByIdQuery($id: Long!, $locale: TemplateLocaleInput) {
    templateAndSingleContent(id: $id, locale: $locale) {
      __typename
      ... on TemplateAndContent {
        templateId
        cacheDataFeed
        createdAt
        creatorUser {
          id
          fullName
          avatarUrl
        }
        dataFeedIds
        jsonTestData {
          dataFeedJson
          userJson
          eventJson
        }
        mergeDataFeedContext
        messageType {
          id
          name
          messageChannel {
            id
            messageMedium
            channelType
          }
        }
        name
        referenceDataFeedsByAlias
        isAssociatedWithActiveExperiment
        updatedAt
        updatedByUser {
          id
          fullName
          avatarUrl
        }
        templateType
        shortenLinks
        primaryContent {
          ... on EmailContentDefinition {
            ampHtml
            bccEmails
            beeBuilderMetadata
            ccEmails
            emailContentId
            fromEmail
            fromName
            googleAnalyticsCampaignName
            html
            htmlEditor
            inlineCss
            linkParams {
              key
              value
            }
            locale {
              name
            }
            payload
            plainText
            preheaderText
            replyTo
            subject
          }
          ... on InAppContentDefinition {
            beeBuilderMetadata
            destinations
            expirationDateTime
            expirationDuration
            html
            htmlEditor
            inAppContentId
            inAppDisplaySettings {
              top
              bottom
              left
              right
              bgColor {
                alpha
                hex
              }
              shouldAnimate
            }
            inboxMetadata {
              title
              subtitle
              icon
            }
            locale {
              name
            }
            messageFormat
            payload
            ottPayload {
              backgroundColor
              dialogText
              dialogHeader
              fhdImage
              dialogBodyFont
              dialogButtonFont
              dialogHeaderFont
              buttons {
                buttonTextColorFocused
                buttonColorFocused
                buttonTextColorUnFocused
                buttonDeeplink
                buttonColorUnFocused
                buttonText
                buttonBorderColor
              }
            }
            saveToInbox
            trigger
            webInAppDisplaySettings
          }
          ... on PushContentDefinition {
            actionButtons {
              identifier
              title
              buttonType
              action {
                type
                data
              }
              openApp
              requiresUnlock
              inputPlaceholder
              inputTitle
              actionIcon {
                iconType
                imageName
              }
            }
            attachmentUrlAndroid
            attachmentUrlIos
            badge
            category
            contentAvailable
            deeplink {
              ios
              android
            }
            defaultAction {
              type
              data
            }
            interruptionLevel
            locale {
              name
            }
            message
            payload
            pushContentId
            relevanceScore
            scoreId
            sound
            title
            isSilentPush
          }
          ... on SMSContentDefinition {
            message
            smsSender {
              id
              name
              phoneNumber
              type
            }
            verifiedContactCardId
            smsContentId
            googleAnalyticsCampaignName
            imageUrl
            linkParams {
              key
              value
            }
            locale {
              name
            }
            trackingDomain
            optOutDisclaimer {
              ... on OnEverySMS {
                message
              }
              ... on OnFirstSMS {
                message
              }
              ... on Disable {
                _
              }
            }
          }
          ... on WebPushContentDefinition {
            webPushContentId
            message
            title
            locale {
              name
            }
            clickAction
            icon
          }
          ... on EmbeddedContentDefinition {
            embeddedContentId
            locale {
              name
            }
            title
            body
            elements {
              mediaUrl
              defaultAction {
                type
                data
              }
              buttons {
                id
                title
                action {
                  type
                  data
                }
              }
              text {
                id
                label
                text
              }
            }
            listId
            placementId
            payload
          }
          ... on WhatsAppContentDefinition {
            message
            locale {
              name
            }
            header {
              ... on TextHeader {
                text
              }
              ... on ImageHeader {
                mediaUrl
              }
            }
            footer
            buttons {
              ... on UrlButton {
                buttonText
                title
                url
              }
              ... on CallButton {
                buttonText
                title
                phoneNumber
              }
              ... on QuickReplyButton {
                buttonText
                title
              }
            }
            whatsAppCategory: category
            previousCategory
            metaApprovalStatus
            qualityRating
            rejectionReason
            whatsAppSenderId
            whatsAppContentId
            trackingDomain
            linkParams {
              key
              value
            }
            googleAnalyticsCampaignName
          }
        }
      }
      ... on CorruptedTemplateData {
        contentId
      }
    }
  }
`;

export const transformTemplateData = (
  templateQueryData: any,
  campaign: TemplateCampaign,
  {
    refetch,
    previousData,
    errorMessage,
    hasError,
    isLoading,
    isRokuTemplatingOn,
  }: Record<string, any>,
) => {
  // protect against the undefined response while the query is running
  if (isDefined<FetchTemplateSingleContentByIdQueryResponse>(templateQueryData)) {
    const responseData = templateQueryData.templateAndSingleContent || {};

    if (responseData.__typename === GqlTemplateResponseTypes.NO_TEMPLATE_FOUND) {
      return {
        errorMessage: responseData.__typename,
        hasError: true,
        isLoading: false,
        isAssociatedWithActiveExperiment: false,
        medium: UnselectedMessageMedium.Unselected,
        messageTypeId: -1,
        templateType: undefined,
        previousDataLocale: undefined,
        previousDataTemplateId: undefined,
        refetch,
        results: '{}',
      };
    } else if (responseData.__typename === GqlTemplateResponseTypes.CORRUPTED_TEMPLATE_DATA) {
      return {
        errorMessage: responseData.__typename,
        hasError: true,
        isLoading: false,
        isAssociatedWithActiveExperiment: false,
        medium: UnselectedMessageMedium.Unselected,
        messageTypeId: -1,
        templateType: undefined,
        previousDataLocale: previousData?.templateAndSingleContent?.primaryContent?.locale?.name,
        previousDataTemplateId: previousData?.templateAndSingleContent?.templateId,
        refetch,
        results: '{}',
      };
    } else if ('primaryContent' in responseData) {
      const { primaryContent, ...commonFields } = responseData as TemplateAndContent;
      const medium =
        commonFields.messageType?.messageChannel?.messageMedium ||
        UnselectedMessageMedium.Unselected;
      const messageTypeId = commonFields.messageType?.id || -1;
      return {
        errorMessage,
        hasError,
        isLoading,
        isAssociatedWithActiveExperiment: commonFields.isAssociatedWithActiveExperiment,
        medium,
        messageTypeId,
        templateType: commonFields.templateType,
        previousDataLocale: previousData?.templateAndSingleContent?.primaryContent?.locale?.name,
        previousDataTemplateId: previousData?.templateAndSingleContent?.templateId,
        refetch,
        results: prepInitialData(
          medium,
          commonFields,
          primaryContent ?? {},
          campaign,
          isRokuTemplatingOn,
        ),
      };
    }
  }

  return {
    errorMessage,
    hasError,
    isLoading,
    isAssociatedWithActiveExperiment: false,
    medium: UnselectedMessageMedium.Unselected,
    messageTypeId: -1,
    templateType: undefined,
    previousDataLocale: undefined,
    previousDataTemplateId: undefined,
    refetch,
    results: '{}',
  };
};

export function useTemplateSingleContentEditById(
  options: Record<string, any>,
  query: DocumentNode = fetchTemplateSingleContentByIdQuery, // eslint-disable-line
): UseTemplateEditSingleContentById {
  const primitiveOptions = JSON.stringify(options);
  const isRokuTemplatingOn = useSelector(selectIsRokuTemplatingFeatureFlagOn);
  const { campaign } = useCampaignQueryContext();

  const queryOptions = useMemo(() => {
    const objOptions = JSON.parse(primitiveOptions);
    return {
      notifyOnNetworkStatusChange: true,
      ...objOptions,
    };
  }, [primitiveOptions]);
  const {
    data: fetchTemplateByIdQueryData,
    previousData,
    error: hasError = false,
    errorMessage,
    isLoading,
    refetch,
  } = useGraphql(fetchTemplateSingleContentByIdQuery, queryOptions);

  return transformTemplateData(fetchTemplateByIdQueryData, campaign, {
    refetch,
    previousData,
    errorMessage,
    hasError,
    isLoading,
    isRokuTemplatingOn,
  });
}
